/**
 * metre-checkout-b.js  —  Scenario B: Direct Razorpay Checkout
 *
 * Flow:
 * 1. Intercepts checkout button
 * 2. Shows order summary popup with product thumbnails
 * 3. On "Pay" click → opens Razorpay modal
 * 4. Verifies payment → creates Shopify order → shows confirmation
 */

(function () {
  "use strict";

  const CREATE_ORDER_URL =
    "https://checkout-api-zeta.vercel.app/api/create-razorpay-order";
  const VERIFY_URL = "https://checkout-api-zeta.vercel.app/api/verify-payment";
  // const RAZORPAY_SDK_URL = "https://checkout.razorpay.com/v1/checkout.js";
  const RAZORPAY_SDK_URL =
    "https://cdn.razorpay.com/static/widget/one-click-checkout.js";

  // ── Helpers ────────────────────────────────────────────────────────────────

  async function getCart() {
    const res = await fetch("/cart.js");
    return res.json();
  }

  function buildLines(cart) {
    return cart.items.map((item) => {
      const rawNetLen =
        item.properties?.["Net length"] || item.properties?.["length"] || "";
      const metres = parseFloat(rawNetLen);
      // Only treat as a metre item when the property is present and has a valid length
      const isPcs = !rawNetLen || isNaN(metres) || metres <= 0;

      const unitPrice = (item.price / 100).toFixed(2);

      const properties = Object.entries(item.properties || {})
        .filter(([k]) => k !== "Net length" && k !== "length")
        .map(([name, value]) => ({ name, value }));

      return {
        variantId: item.variant_id,
        title: item.product_title || item.title,
        image: item.featured_image?.url || item.image || "",
        // For metre items netLength drives the total; for PCS use actual cart qty
        netLength: isPcs ? String(item.quantity) : rawNetLen,
        unitPrice,
        quantity: item.quantity,
        isPcs,
        properties,
      };
    });
  }

  function formatINR(amount) {
    return (
      "₹" +
      parseFloat(amount).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    );
  }

  function loadRazorpaySDK() {
    return new Promise((resolve, reject) => {
      if (window.Razorpay) return resolve();
      const script = document.createElement("script");
      script.src = RAZORPAY_SDK_URL;
      script.onload = resolve;
      script.onerror = () => reject(new Error("Failed to load Razorpay SDK"));
      document.head.appendChild(script);
    });
  }

  function setLoading(btn, loading) {
    if (!btn) return;
    if (loading) {
      btn._origText = btn._origText || btn.textContent;
      btn.disabled = true;
      btn.textContent = "Loading…";
    } else {
      btn.disabled = false;
      btn.textContent = btn._origText || btn.textContent;
    }
  }

  // ── Order Summary Popup ────────────────────────────────────────────────────

  function showOrderSummary(lines, onPay, onClose) {
    const existing = document.getElementById("mw-order-summary");
    if (existing) existing.remove();

    const total = lines.reduce((sum, l) => {
      return sum + parseFloat(l.netLength) * parseFloat(l.unitPrice);
    }, 0);

    const itemsHTML = lines
      .map((line) => {
        const lineTotal = (
          parseFloat(line.netLength) * parseFloat(line.unitPrice)
        ).toFixed(2);
        const imgHTML = line.image
          ? `<img src="${line.image}" alt="${line.title}"
               style="width:64px;height:64px;object-fit:cover;border-radius:8px;
                      border:1px solid #eee;flex-shrink:0;">`
          : `<div style="width:64px;height:64px;background:#f0f0f0;border-radius:8px;
                       display:flex;align-items:center;justify-content:center;
                       font-size:22px;flex-shrink:0;">🧵</div>`;
        // Metre items: show "Net length: X m | ₹Y per metre"
        // PCS items:   show "Qty: N | ₹Y"
        const detailsHTML = line.isPcs
          ? `<div style="font-size:13px;color:#888;margin-top:4px;">Qty: ${line.quantity}</div>
           <div style="font-size:13px;color:#888;">${formatINR(line.unitPrice)}</div>`
          : `<div style="font-size:13px;color:#888;margin-top:4px;">Net length: ${line.netLength}</div>
           <div style="font-size:13px;color:#888;">${formatINR(line.unitPrice)} per metre</div>`;
        return `
        <div style="display:flex;gap:14px;align-items:center;
                    padding:14px 0;border-bottom:1px solid #f0f0f0;">
          ${imgHTML}
          <div style="flex:1;min-width:0;">
            <div style="font-weight:600;font-size:14px;color:#1a1a2e;
                        white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">
              ${line.title}
            </div>
            ${detailsHTML}
          </div>
          <div style="font-weight:700;font-size:15px;color:#1a1a2e;white-space:nowrap;">
            ${formatINR(lineTotal)}
          </div>
        </div>`;
      })
      .join("");

    const inputStyle = `width:100%;padding:9px 12px;border:1px solid #ddd;border-radius:6px;
                        font-size:14px;box-sizing:border-box;margin-top:5px;`;
    const labelStyle = `font-size:12px;font-weight:600;color:#555;display:block;margin-top:10px;`;

    const cust = window.mwCustomer || {};

    const popup = document.createElement("div");
    popup.id = "mw-order-summary";
    popup.style.cssText = `
      position:fixed;top:0;left:0;width:100%;height:100%;
      background:rgba(0,0,0,0.55);z-index:99999;
      display:flex;align-items:center;justify-content:center;
      font-family:Arial,sans-serif;
    `;

    popup.innerHTML = `
      <div style="background:#fff;border-radius:14px;width:92%;max-width:460px;
                  max-height:90vh;overflow-y:auto;box-shadow:0 20px 60px rgba(0,0,0,0.3);">

        <!-- Header -->
        <div style="display:flex;justify-content:space-between;align-items:center;
                    padding:18px 20px;border-bottom:1px solid #eee;position:sticky;
                    top:0;background:#fff;z-index:1;">
          <h3 style="margin:0;font-size:17px;color:#1a1a2e;">Order Summary</h3>
          <button id="mw-close-btn"
                  style="background:none;border:none;font-size:22px;cursor:pointer;
                         color:#999;line-height:1;padding:0;">✕</button>
        </div>

        <!-- Items -->
        <div style="padding:0 20px;">${itemsHTML}</div>

        <!-- Total -->
        <div style="display:flex;justify-content:space-between;align-items:center;
                    padding:16px 20px;background:#f8f8f8;border-top:1px solid #eee;">
          <span style="font-size:16px;font-weight:600;color:#1a1a2e;">Total</span>
          <span style="font-size:20px;font-weight:700;color:#1a1a2e;">
            ${formatINR(total.toFixed(2))}
          </span>
        </div>

        <!-- Delivery Details -->
        <div style="padding:16px 20px;border-top:1px solid #eee;">
          <div style="font-size:14px;font-weight:700;color:#1a1a2e;margin-bottom:4px;">
            Delivery Details
          </div>
          <label style="${labelStyle}">Full Name *</label>
          <input id="mw-addr-name" type="text" placeholder="Your full name"
                 value="${cust.name || ""}" style="${inputStyle}">

          <label style="${labelStyle}">Phone *</label>
          <input id="mw-addr-phone" type="tel" placeholder="+91 9876543210"
                 value="${cust.phone || ""}" style="${inputStyle}">

          <label style="${labelStyle}">Address Line 1 *</label>
          <input id="mw-addr-line1" type="text" placeholder="House / Flat / Street"
                 value="${cust.address1 || ""}" style="${inputStyle}">

          <label style="${labelStyle}">Address Line 2</label>
          <input id="mw-addr-line2" type="text" placeholder="Area / Landmark (optional)"
                 value="${cust.address2 || ""}" style="${inputStyle}">

          <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;">
            <div>
              <label style="${labelStyle}">City *</label>
              <input id="mw-addr-city" type="text" placeholder="City"
                     value="${cust.city || ""}" style="${inputStyle}">
            </div>
            <div>
              <label style="${labelStyle}">Pincode *</label>
              <input id="mw-addr-pin" type="text" placeholder="400001" maxlength="6"
                     value="${cust.zip || ""}" style="${inputStyle}">
            </div>
          </div>

          <label style="${labelStyle}">State *</label>
          <input id="mw-addr-state" type="text" placeholder="Maharashtra"
                 value="${cust.province || ""}" style="${inputStyle}">
        </div>

        <!-- Pay Button -->
        <div style="padding:16px 20px;">
          <button id="mw-pay-btn"
                  style="width:100%;background:#1a1a2e;color:#fff;border:none;
                         border-radius:8px;padding:16px;font-size:16px;font-weight:600;
                         cursor:pointer;display:flex;align-items:center;
                         justify-content:center;gap:10px;">
            <img src="https://razorpay.com/favicon.ico"
                 style="width:20px;height:20px;border-radius:4px;"
                 onerror="this.style.display='none'">
            Pay ${formatINR(total.toFixed(2))} with Razorpay
          </button>
          <p style="text-align:center;font-size:12px;color:#aaa;margin:10px 0 0;">
            UPI · Cards · Net Banking · Wallets · Cash on Delivery
          </p>
        </div>
      </div>
    `;

    document.body.appendChild(popup);

    function collectAddress() {
      const name = document.getElementById("mw-addr-name").value.trim();
      const phone = document.getElementById("mw-addr-phone").value.trim();
      const line1 = document.getElementById("mw-addr-line1").value.trim();
      const city = document.getElementById("mw-addr-city").value.trim();
      const pin = document.getElementById("mw-addr-pin").value.trim();
      const state = document.getElementById("mw-addr-state").value.trim();
      if (!name || !phone || !line1 || !city || !pin || !state) {
        alert("Please fill in all required delivery details.");
        return null;
      }
      const nameParts = name.split(" ");
      return {
        first_name: nameParts[0],
        last_name: nameParts.slice(1).join(" ") || "",
        phone,
        address1: line1,
        address2: document.getElementById("mw-addr-line2").value.trim(),
        city,
        zip: pin,
        province: state,
        country: "India",
        country_code: "IN",
      };
    }

    document.getElementById("mw-close-btn").onclick = () => {
      popup.remove();
      onClose();
    };

    document.getElementById("mw-pay-btn").onclick = () => {
      const addr = collectAddress();
      if (!addr) return;
      popup.remove();
      onPay(addr);
    };

    popup.addEventListener("click", (e) => {
      if (e.target === popup) {
        popup.remove();
        onClose();
      }
    });
  }

  // ── Success Screen ─────────────────────────────────────────────────────────

  function showSuccess(orderName, paymentId) {
    const existing = document.getElementById("mw-order-summary");
    if (existing) existing.remove();

    const icon = "✅";
    const subLine = `<p style="color:#aaa;font-size:12px;margin:0 0 20px;">Payment ID: ${paymentId}</p>`;

    const overlay = document.createElement("div");
    overlay.style.cssText = `
      position:fixed;top:0;left:0;width:100%;height:100%;
      background:rgba(0,0,0,0.6);z-index:99999;
      display:flex;align-items:center;justify-content:center;
      font-family:Arial,sans-serif;
    `;
    overlay.innerHTML = `
      <div style="background:#fff;border-radius:14px;padding:40px 32px;
                  max-width:400px;width:90%;text-align:center;">
        <div style="font-size:52px;margin-bottom:16px;">✅</div>
        <h2 style="margin:0 0 10px;color:#1a1a2e;font-size:22px;">Order Placed!</h2>
        <p style="color:#555;margin:0 0 6px;font-size:15px;">
          Order: <strong>${orderName}</strong>
        </p>
        ${subLine}
        <p style="color:#666;font-size:14px;margin:0 0 28px;">
          A confirmation has been sent to you.
        </p>
        <a href="/collections/all"
           style="display:inline-block;background:#1a1a2e;color:#fff;
                  padding:13px 32px;border-radius:8px;text-decoration:none;
                  font-size:15px;font-weight:600;">
          Continue Shopping
        </a>
      </div>
    `;
    document.body.appendChild(overlay);
  }

  // ── Core checkout flow ─────────────────────────────────────────────────────

  /**
   * Fetch variant price directly from Shopify storefront API.
   * Most reliable — no DOM parsing needed.
   */
  async function getVariantUnitPrice(variantId) {
    const res = await fetch(`/variants/${variantId}.js`);
    const data = await res.json();
    return data.price / 100; // Shopify returns price in paise/cents
  }

  /**
   * Build line items directly from the product form (for "Buy it now").
   * The form must be the one that metre-selector.js injected into — it contains:
   *   [name="id"]                        → selected variant ID
   *   [name="properties[Net length]"]    → selected metres, e.g. "2.5 m"
   *   [name="quantity"] (lockedQty=1)    → always 1 (we use Net length, not qty)
   */
  async function buildLinesFromForm(form) {
    const variantId = form.querySelector('[name="id"]')?.value;
    if (!variantId)
      throw new Error("buildLinesFromForm: no variant id in form");

    // The hidden input injected by metre-selector.js — value like "2.5 m"
    const netLengthInput =
      form.querySelector('[name="properties[Net length]"]') ||
      form.querySelector('[name="properties[length]"]');
    const rawNetLength = netLengthInput?.value?.trim() || "";

    const metres = parseFloat(rawNetLength);
    const isMetreItem = !!rawNetLength && !isNaN(metres) && metres > 0;

    // Fetch unit price from Shopify storefront API — most reliable source
    const unitPrice = await getVariantUnitPrice(variantId);
    if (!unitPrice || isNaN(unitPrice)) {
      throw new Error(
        `Buy it now: could not fetch unit price for variant ${variantId}`,
      );
    }

    // Get product title (search within the product section first, then page)
    const productSection =
      form.closest(
        "section, .product-single, .product-info, .product__info-wrapper, [data-section-type]",
      ) || document.body;
    const title =
      productSection
        .querySelector("h1, .product__title, [data-product-title]")
        ?.textContent?.trim() ||
      document
        .querySelector(
          "h1.product-single__title, [data-product-title], h1.title",
        )
        ?.textContent?.trim() ||
      "Product";

    // Get product image
    const image =
      productSection.querySelector("img")?.src ||
      document.querySelector(
        ".product__media img, .product-single__photo img, .product-featured-img",
      )?.src ||
      "";

    // Collect any other line properties (e.g. colour swatch selections)
    const properties = [];
    form.querySelectorAll('[name^="properties["]').forEach((input) => {
      const key = input.name.match(/\[(.+)\]/)?.[1];
      if (key && key !== "Net length" && key !== "length" && input.value) {
        properties.push({ name: key, value: input.value });
      }
    });

    if (isMetreItem) {
      // Metre product: quantity locked to 1, netLength drives the total
      if (!rawNetLength) {
        throw new Error(
          "Buy it now: Net length not set. Please select a length from the dropdown first.",
        );
      }
      console.log(
        "[metre-checkout-b] buildLinesFromForm (metre) →",
        "variantId:",
        variantId,
        "| netLength:",
        rawNetLength,
        "| unitPrice: ₹" + unitPrice.toFixed(2),
        "| total: ₹" + (metres * unitPrice).toFixed(2),
      );
      return [
        {
          variantId,
          title,
          image,
          netLength: rawNetLength,
          unitPrice: unitPrice.toFixed(2),
          quantity: 1,
          isPcs: false,
          properties,
        },
      ];
    } else {
      // PCS product: use the form's quantity input
      const qty =
        parseInt(form.querySelector('[name="quantity"]')?.value, 10) || 1;
      console.log(
        "[metre-checkout-b] buildLinesFromForm (PCS) →",
        "variantId:",
        variantId,
        "| qty:",
        qty,
        "| unitPrice: ₹" + unitPrice.toFixed(2),
        "| total: ₹" + (qty * unitPrice).toFixed(2),
      );
      return [
        {
          variantId,
          title,
          image,
          netLength: String(qty), // backend uses parseFloat(netLength) × unitPrice
          unitPrice: unitPrice.toFixed(2),
          quantity: qty,
          isPcs: true,
          properties,
        },
      ];
    }
  }

  async function interceptCheckout(e, btn) {
    e.preventDefault();
    e.stopImmediatePropagation();
    setLoading(btn, true);

    try {
      // ── 1. Build line items ──────────────────────────────────────────────────
      const isBuyItNow =
        btn.classList.contains("shopify-payment-button__button") ||
        !!btn.closest("shopify-buy-it-now-button") ||
        btn.classList.contains("mw-buynow");

      const productForm =
        btn._mwForm ||
        btn.closest("form[action*='/cart/add']") ||
        document.querySelector("form[action*='/cart/add']");

      let lines;
      if (isBuyItNow && productForm) {
        lines = await buildLinesFromForm(productForm);
      } else {
        const cart = await getCart();
        lines = buildLines(cart);
      }

      setLoading(btn, false);

      // ── 2. Show order summary — customer confirms then pays via Razorpay ───────
      let shippingAddress = null;

      await new Promise((resolve) => {
        showOrderSummary(
          lines,
          (addr) => {
            shippingAddress = addr;
            resolve();
          },
          () => resolve(),
        );
      });

      if (!shippingAddress) {
        setLoading(btn, false);
        return;
      }
      setLoading(btn, true);

      // ── 3. Razorpay payment ───────────────────────────────────────────────────
      const orderRes = await fetch(CREATE_ORDER_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ lines }),
      });
      const orderData = await orderRes.json();
      if (!orderRes.ok || !orderData.orderId) {
        throw new Error(orderData.error || "Failed to create payment order");
      }

      await loadRazorpaySDK();
      setLoading(btn, false);

      await new Promise((resolve, reject) => {
        const rzp = new window.Razorpay({
          key: orderData.keyId,
          amount: orderData.amount,
          currency: orderData.currency,
          order_id: orderData.orderId,
          name: "Matching Woman",
          description: `${lines.length} item(s)`,
          theme: { color: "#1a1a2e" },
          prefill: {
            name: `${shippingAddress.first_name} ${shippingAddress.last_name}`.trim(),
            contact: shippingAddress.phone,
          },
          method: { cod: 1 },

          handler: async function (response) {
            try {
              setLoading(btn, true);
              btn.textContent = "Confirming order…";

              const verifyRes = await fetch(VERIFY_URL, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature,
                  lines: orderData.lines,
                  shippingAddress,
                }),
              });
              const verifyData = await verifyRes.json();
              setLoading(btn, false);

              if (!verifyRes.ok || !verifyData.success) {
                throw new Error(verifyData.error || "Order creation failed");
              }

              showSuccess(verifyData.orderName, verifyData.paymentId);
              resolve();
            } catch (err) {
              reject(err);
            }
          },

          modal: {
            ondismiss: () => {
              setLoading(btn, false);
              resolve();
            },
          },
        });

        rzp.on("payment.failed", (r) => {
          reject(new Error(r.error?.description || "Payment failed"));
        });
        rzp.open();
      });
    } catch (err) {
      console.error("[metre-checkout-b] Error:", err);
      setLoading(btn, false);
      alert("Error: " + err.message + "\nPlease try again.");
    }
  }

  // ── Event delegation ───────────────────────────────────────────────────────

  const CHECKOUT_SELECTORS = [
    'button[name="checkout"]',
    'input[name="checkout"]',
    'button[type="submit"].cart__checkout-button',
    'button[type="submit"].cart-checkout-button',
    ".button-checkout",
    "[data-checkout-button]",
    "#checkout",
    // Shopify dynamic checkout / Buy it now button
    ".shopify-payment-button__button",
    "shopify-buy-it-now-button button",
    // Our custom Buy it now button injected by metre-selector.js
    ".mw-buynow",
  ];

  function isCheckoutTrigger(el) {
    return CHECKOUT_SELECTORS.some((sel) => el.matches(sel));
  }

  document.addEventListener(
    "click",
    (e) => {
      let el = e.target;
      while (el && el !== document.body) {
        if (isCheckoutTrigger(el)) {
          interceptCheckout(e, el);
          return;
        }
        el = el.parentElement;
      }
    },
    { capture: true },
  );

  document.addEventListener(
    "submit",
    (e) => {
      if (e.target.action?.includes("/checkout")) {
        const btn = e.target.querySelector('[type="submit"]') || e.target;
        interceptCheckout(e, btn);
      }
    },
    { capture: true },
  );

  // ── Expose for direct calls from metre-selector.js ─────────────────────────
  // metre-selector.js injects a .mw-buynow button whose click handler calls
  // window.mwCheckout.startBuyNow(form) directly so it works even when event
  // delegation hasn't fired (e.g. page load timing differences).
  window.mwCheckout = window.mwCheckout || {};
  window.mwCheckout.startBuyNow = async function (form) {
    if (!form) return;
    // Synthetic button: _mwForm is the key property — interceptCheckout reads it to
    // locate the correct form (the button lives outside the <form> element).
    const fakeBtn = {
      _mwForm: form, // ← correct form reference
      closest: (sel) => form.closest(sel) || form,
      classList: { contains: (c) => c === "mw-buynow" },
      _origText: "Buy it now",
      disabled: false,
      textContent: "Buy it now",
    };
    const fakeEvent = {
      preventDefault: () => {},
      stopImmediatePropagation: () => {},
    };
    await interceptCheckout(fakeEvent, fakeBtn);
  };
  // Redirect Add to Cart → Product page on collection pages
if (!window.location.pathname.includes("/products/")) {
  document.addEventListener("click", (e) => {
    const btn = e.target.closest(".globo--add-to-cart, [data-globo-cart-bound]");
    if (!btn) return;

    e.preventDefault();
    e.stopImmediatePropagation();

    const card = btn.closest("[data-json-product]");
    let handle = null;
    if (card) {
      try { handle = JSON.parse(card.dataset.jsonProduct)?.handle; } catch { /* */ }
    }

    const link = btn.closest(".product-item, li, article")
      ?.querySelector("a[href*='/products/']")
      ?.getAttribute("href");

    const dest = handle ? `/products/${handle}` : link;
    if (dest) window.location.href = dest;
  }, true);
}
})();
